import type { Voicing } from '../../domain'

/**
 * Diagrama de acorde en SVG propio: seis cuerdas, cinco trastes, puntos, cejilla y marcas de
 * cuerda al aire o muda. Usa currentColor para seguir el tema.
 */
interface ChordDiagramProps {
  voicing: Voicing
  /** Ancho en píxeles CSS; el alto es proporcional. */
  size?: number
  label?: string
}

const STRINGS = 6
const FRETS = 5
const LEFT = 18
const TOP = 26
const GAP_X = 16
const GAP_Y = 20
const WIDTH = LEFT * 2 + GAP_X * (STRINGS - 1)
const HEIGHT = TOP + GAP_Y * FRETS + 12

export function ChordDiagram({ voicing, size = 120, label }: ChordDiagramProps) {
  const fretted = voicing.frets.filter((fret): fret is number => fret !== 'x' && fret > 0)
  const maxFret = Math.max(0, ...fretted)
  const baseFret = maxFret > FRETS ? Math.min(...fretted) : 1
  const x = (stringIndex: number) => LEFT + stringIndex * GAP_X
  const y = (fret: number) => TOP + (fret - baseFret + 0.5) * GAP_Y

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width={size}
      height={(size * HEIGHT) / WIDTH}
      role="img"
      aria-label={label}
      style={{ display: 'block' }}
    >
      {baseFret === 1 ? (
        <rect
          x={LEFT - 1}
          y={TOP - 4}
          width={GAP_X * (STRINGS - 1) + 2}
          height={4}
          fill="currentColor"
        />
      ) : (
        <text x={4} y={TOP + GAP_Y * 0.65} fontSize={11} fill="currentColor">
          {baseFret}
        </text>
      )}
      {Array.from({ length: FRETS + 1 }, (_, fret) => (
        <line
          key={`f${fret}`}
          x1={LEFT}
          x2={LEFT + GAP_X * (STRINGS - 1)}
          y1={TOP + fret * GAP_Y}
          y2={TOP + fret * GAP_Y}
          stroke="currentColor"
          strokeOpacity={0.5}
        />
      ))}
      {Array.from({ length: STRINGS }, (_, string) => (
        <line
          key={`s${string}`}
          x1={x(string)}
          x2={x(string)}
          y1={TOP}
          y2={TOP + GAP_Y * FRETS}
          stroke="currentColor"
          strokeOpacity={0.7}
        />
      ))}
      {voicing.barre && (
        <rect
          x={x(STRINGS - voicing.barre.fromString) - 6}
          y={y(voicing.barre.fret) - 6}
          width={x(STRINGS - voicing.barre.toString) - x(STRINGS - voicing.barre.fromString) + 12}
          height={12}
          rx={6}
          fill="currentColor"
        />
      )}
      {voicing.frets.map((fret, string) => {
        if (fret === 'x') {
          return (
            <text
              key={`m${string}`}
              x={x(string)}
              y={TOP - 9}
              fontSize={11}
              textAnchor="middle"
              fill="currentColor"
            >
              ×
            </text>
          )
        }
        if (fret === 0) {
          return (
            <circle
              key={`m${string}`}
              cx={x(string)}
              cy={TOP - 13}
              r={4}
              fill="none"
              stroke="currentColor"
            />
          )
        }
        if (voicing.barre && fret === voicing.barre.fret) return null
        return <circle key={`m${string}`} cx={x(string)} cy={y(fret)} r={6} fill="currentColor" />
      })}
    </svg>
  )
}
